import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import { Edit, Replay, EventRepeat } from "@mui/icons-material";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import { rotate360 } from "../utils/utils";
import { Transaction } from "../utils/types";
import { translate } from "../utils/currency";
import StatusPill from "./StatusPill";

const money = (value: number | null | undefined) =>
  Number(value ?? 0).toLocaleString("ru-RU");

const operationLabels: Record<string, string> = {
  deposit: "Приём",
  payout: "Выплата",
  withdrawal: "Вывод",
  cashin: "Пополнение",
  transfer: "Перевод",
};

/** Одна пара «подпись — значение» в сетке реквизитов. */
const Field: React.FC<{
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  wide?: boolean;
  onCopy?: () => void;
}> = ({ label, value, mono, wide, onCopy }) => (
  <Box sx={{ minWidth: 0, gridColumn: wide ? "1 / -1" : undefined }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: "block",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: ".05em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 560,
          fontFamily: mono ? "monospace" : undefined,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "—"}
      </Typography>
      {onCopy && (
        <IconButton
          aria-label={`Скопировать: ${label.toLowerCase()}`}
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onCopy();
          }}
          // Карточка живёт только на телефоне — цель должна быть пальцевой.
          sx={{ width: 34, height: 34, flexShrink: 0 }}
        >
          <ContentCopyRounded sx={{ fontSize: 15 }} />
        </IconButton>
      )}
    </Box>
  </Box>
);

export const TransactionCard: React.FC<{
  t: Transaction;
  merchName: string;
  providerName: string;
  onEdit: any;
  onCallback: any;
  idType: string;
  /** Нажатие по карточке раскрывает подробности операции. */
  onOpenDetails?: () => void;
}> = ({ t, merchName, providerName, onEdit, onCallback, onOpenDetails }) => {
  const theme = useTheme();
  const copy = (value: string) => navigator.clipboard.writeText(value);

  const created = new Date(t.created_at);
  const updated = t.updated_at ? new Date(t.updated_at) : null;
  const settled = Boolean(t.paid_amount);

  return (
    <Paper
      // Кнопки внутри карточки останавливают всплытие сами, поэтому нажатие
      // по свободному месту безопасно открывает подробности.
      onClick={onOpenDetails}
      role={onOpenDetails ? "button" : undefined}
      tabIndex={onOpenDetails ? 0 : undefined}
      onKeyDown={(event) => {
        if (onOpenDetails && event.key === "Enter") onOpenDetails();
      }}
      sx={{
        p: 2,
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        cursor: onOpenDetails ? "pointer" : undefined,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ mb: 0.75 }}>
            <StatusPill status={t.status} />
          </Box>
          <Typography sx={{ fontSize: 14.5, fontWeight: 680 }} noWrap>
            {merchName || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {operationLabels[t.transaction_type] || t.transaction_type}
          </Typography>
        </Box>

        <Box textAlign="right" sx={{ flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 740,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {money(t.start_amount)}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.5, fontWeight: 700 }}
            >
              {t.currency}
            </Typography>
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: settled
                ? theme.palette.semanticText.success
                : "text.secondary",
            }}
          >
            подтв. {money(t.paid_amount)}
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          rowGap: 1.25,
          columnGap: 1.5,
        }}
      >
        <Field
          label="ID транзакции"
          value={t.id}
          mono
          wide
          onCopy={() => copy(t.id)}
        />
        <Field
          label="Заказ мерчанта"
          value={t.merchant_order_id}
          mono
          onCopy={() => copy(t.merchant_order_id || "")}
        />
        <Field
          label="Заказ провайдера"
          value={t.mid_order_id}
          mono
          onCopy={() => copy(t.mid_order_id || "")}
        />
        <Field
          label="Реквизиты"
          value={t.payment_method === "bol_qr_code" ? t.qr_code : t.number}
          mono
        />
        <Field label="ФИО" value={t.fullname} />
        <Field label="Метод" value={translate(t.payment_method)} />
        <Field label="Банк" value={t.bank_name} />
        <Field label="Провайдер" value={providerName} />
        <Field label="Пользователь" value={t.user_id} mono />
        <Field
          label="Ставка / комиссия"
          value={`${t.our_rate ?? "—"}% / ${money(t.our_fee)}`}
        />
      </Box>

      <Divider />

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="caption" color="text.secondary">
            {created.toLocaleDateString("ru-RU")}{" "}
            {created.toLocaleTimeString("ru-RU", { hour12: false })}
          </Typography>
          {updated && (
            <Tooltip
              title={`Обновлено: ${updated.toLocaleDateString("ru-RU")} ${updated.toLocaleTimeString("ru-RU", { hour12: false })}`}
            >
              <EventRepeat sx={{ fontSize: 14, color: "text.secondary" }} />
            </Tooltip>
          )}
        </Box>

        <Box display="flex" gap={0.5}>
          <Tooltip title="Редактировать">
            <IconButton
              aria-label="Редактировать транзакцию"
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: theme.palette.info.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.info.main, 0.12),
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(e);
              }}
            >
              <Edit fontSize="inherit" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Отправить колбэк">
            <IconButton
              aria-label="Отправить колбэк"
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: theme.palette.warning.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.warning.main, 0.12),
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onCallback(e);
              }}
            >
              <Replay
                fontSize="inherit"
                sx={{
                  "&:hover": {
                    animation: `${rotate360} 1.4s linear infinite`,
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};
